import { useRef, useState, useCallback, useEffect } from 'react';

import type { IAssistantVoiceByLanguage } from '@mytaverse-shared/interfaces/assistantScripts';
import {
  DEFAULT_ENGINE,
  DEFAULT_VOICE_ID,
  PollyEngine,
  PollyLanguage,
} from '@mytaverse-shared/interfaces/polly';
import { getVoices, getAvailableEngines } from '../constants';
import { AudioBufferPlayer } from '../audioPlayer';

// 'test' is reserved for callers outside this dialog; numeric keys identify a
// dialogue step by index; `lang:<PollyLanguage>` identifies a per-language row
// preview (e.g. the steps-scope Language tabs), which isn't tied to any single
// step index.
export type AudioPlaybackKey = 'test' | number | `lang:${string}`;

interface UseAudioPreviewParams {
  activeLanguage: PollyLanguage;
  effectiveBaseLanguage: PollyLanguage;
  engine?: string;
  voiceId?: string;
  voicesByLanguage?: Partial<Record<PollyLanguage, IAssistantVoiceByLanguage>>;
  onGenerateAudio: (params: {
    text: string;
    voiceId: string;
    engine: string;
    language?: string;
  }) => Promise<ArrayBuffer>;
}

const useAudioPreview = ({
  activeLanguage,
  effectiveBaseLanguage,
  engine,
  voiceId,
  voicesByLanguage,
  onGenerateAudio,
}: UseAudioPreviewParams) => {
  const [audioPlaybackKey, setAudioPlaybackKey] =
    useState<AudioPlaybackKey | null>(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const audioPlayerRef = useRef<AudioBufferPlayer | null>(null);

  // Resolves the voice/engine for whichever language is being previewed —
  // callers may preview a language other than the currently active tab (e.g.
  // a row in the Language tabs list), so this can't be memoized off a single
  // `activeLanguage` value.
  const resolveVoiceParams = useCallback(
    (language: PollyLanguage): { engine: PollyEngine; voiceId: string } => {
      if (language === effectiveBaseLanguage) {
        return {
          engine: (engine as PollyEngine | undefined) ?? DEFAULT_ENGINE,
          voiceId: voiceId ?? DEFAULT_VOICE_ID,
        };
      }

      const eventOverride = voicesByLanguage?.[language];

      const resolvedEngine = eventOverride?.engine
        ? (eventOverride.engine as PollyEngine)
        : getAvailableEngines(language, 'female')[0] ??
          getAvailableEngines(language)[0] ??
          DEFAULT_ENGINE;

      const resolvedVoiceId = eventOverride?.voiceId
        ? eventOverride.voiceId
        : getVoices(language, resolvedEngine, 'female')[0] ??
          getVoices(language, resolvedEngine)[0] ??
          DEFAULT_VOICE_ID;

      return { engine: resolvedEngine, voiceId: resolvedVoiceId };
    },
    [effectiveBaseLanguage, engine, voiceId, voicesByLanguage],
  );

  const stopAudio = useCallback(() => {
    audioPlayerRef.current?.destroy();
    audioPlayerRef.current = null;
    setIsAudioPlaying(false);
    setAudioPlaybackKey(null);
  }, []);

  // Stop and release audio when the dialog/hook unmounts so a preview can never
  // keep playing in the background after the modal is closed.
  useEffect(() => {
    return () => {
      stopAudio();
    };
  }, [stopAudio]);

  const playAudio = async (
    text: string,
    key: AudioPlaybackKey,
    language: PollyLanguage = activeLanguage,
  ) => {
    setAudioPlaybackKey(key);

    const { engine: resolvedEngine, voiceId: resolvedVoiceId } =
      resolveVoiceParams(language);

    let player: AudioBufferPlayer | null = null;

    try {
      player = new AudioBufferPlayer(undefined, 0.8);
      audioPlayerRef.current = player;

      const audioData = await onGenerateAudio({
        text,
        voiceId: resolvedVoiceId,
        engine: resolvedEngine,
        language,
      });

      // Bail out if playback was stopped (modal closed / unmounted) while the
      // audio was being fetched — otherwise it would start in the background.
      if (audioPlayerRef.current !== player) {
        player.destroy();
        return;
      }

      if (!audioData || audioData.byteLength === 0) {
        console.warn(
          `Error: no audio returned for engine: ${resolvedEngine}, voiceId: ${resolvedVoiceId}`,
        );
        player.destroy();
        audioPlayerRef.current = null;
        setAudioPlaybackKey(null);
        return;
      }

      const ctx = player.getAudioContext();
      const buffer = await ctx.decodeAudioData(audioData);

      if (audioPlayerRef.current !== player) {
        player.destroy();
        return;
      }

      player.load(buffer);
      setIsAudioPlaying(true);
      player.play({
        loop: false,
        onEnded: () => {
          setIsAudioPlaying(false);
          setAudioPlaybackKey(null);
        },
      });
    } catch (error) {
      console.error(error);
      player?.destroy();

      if (player && audioPlayerRef.current === player) {
        audioPlayerRef.current = null;
      }

      setAudioPlaybackKey(null);
    }
  };

  return { audioPlaybackKey, isAudioPlaying, playAudio, stopAudio };
};

export default useAudioPreview;
